/**
 * The answers to a choose-one question, as one comparison (`docs/proposal.md`
 * §7.3, rebuilt at Aaron's step 21 item 4.6).
 *
 * It used to be a card per answer, side by side, with "Compare the visits" as
 * a grid behind a link. At three or four answers the cards were each a third
 * of the width: names wrapped onto two and three lines, so the cards stood at
 * three different heights, what each held was a list to read and hold in your
 * head, and "See what is included" opened a full-width visit inside a narrow
 * card. The grid underneath was the thing people actually decided with. So
 * the grid **is** the section now:
 *
 *   - **Always the full width of the box**, whatever the number of answers:
 *     the first column takes what is left and every answer's column shares
 *     the rest equally, so two answers are two wide columns, not two cards
 *     and a gap.
 *   - **One header per answer**, the same four rows in every column — the
 *     Recommended mark (or its empty space, so the names line up), the name
 *     on at most two balanced lines, the price as it bills, and how many
 *     visits — so every column is the same height by construction.
 *   - **A row per visit, with what it is under its name**: the visit's own
 *     description where the estimator wrote one, its services otherwise. Where
 *     every answer is a single visit (Classic or Premium) the rows are the
 *     services instead, because a diagonal of one tick per visit compares
 *     nothing.
 *   - **A tick or a dash in every cell**, never "Everything in Silver, plus":
 *     the matrix shows the overlap rather than describing it.
 *   - **A Choose button at the foot of every column**, the column's width, and
 *     the chosen column tinted from its header to its button. Tapping anywhere
 *     in a column chooses it.
 *
 * On a phone the same table scrolls sideways under a sticky first column,
 * because a ladder of four answers does not fit in 390px and a row whose name
 * has scrolled away is a row nobody can read.
 */
import {
  type ProposalGroup, type ProposalOption, type ProposalSelection, type ProposalView,
  type ProposalVisit,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { GlyphCheck } from './glyphs.js'
import { ProposalTag } from './tag.js'
import { sayPrice } from './price.js'

export interface OptionCardsProps {
  view: ProposalView
  group: ProposalGroup
  selection: ProposalSelection
  onSelection: (selection: ProposalSelection) => void
  phone: boolean
}

/** One row of the comparison: a visit, or — where every answer is one visit — a service. */
interface MatrixRow {
  key: string
  name: string
  /** What it is, under the name. */
  about: string | null
  /** Which answers hold it. */
  in: Set<string>
}

export function OptionCards({ view, group, selection, onSelection, phone }: OptionCardsProps) {
  const picked = selection.choose[group.id]
  const choose = (optionId: string | null) => onSelection({
    ...selection,
    choose: { ...selection.choose, [group.id]: optionId },
  })
  const rows = rowsOf(view, group)
  const columns = group.options.length
  const template = phone
    ? `minmax(148px, 1.2fr) repeat(${columns}, minmax(112px, 1fr))`
    : `minmax(240px, 1.6fr) repeat(${columns}, minmax(132px, 1fr))`

  /* The chosen column's tint, on every cell of it. */
  const tone = (optionId: string) => (picked === optionId ? 'bg-brand-t1' : '')
  const first = cn('sticky left-0 z-[1] border-r border-line-soft bg-surface',
    phone ? 'px-3.5' : 'px-6')
  const cell = 'border-l border-line-soft first:border-l-0'

  return (
    <div className={cn('flex flex-col', phone ? 'gap-3' : 'gap-4')}>
      <div
        data-choice-matrix={group.id}
        className="overflow-hidden rounded-[18px] border border-line bg-surface shadow-proposal"
      >
        <div className="overflow-x-auto">
          <div
            role="table"
            aria-label={group.name}
            className="grid w-full"
            style={{ gridTemplateColumns: template }}
          >
            {/* Header: the four rows every column carries, so they all stand the same height. */}
            <div role="row" className="contents">
              <div role="columnheader" className={cn(first, 'flex flex-col justify-end pb-4 pt-5')}>
                <span className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-text-faint">
                  What is included
                </span>
              </div>
              {group.options.map((option) => {
                const said = sayPrice(standIn(view, option))
                return (
                  <div key={option.id} role="columnheader"
                    className={cn(cell, 'border-b border-line-soft', tone(option.id),
                      picked === option.id
                        /* A bar, not a border: a 3px top border pushed the chosen header down by 3px. */
                        && 'relative before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-brand-ui')}>
                    <button
                      type="button"
                      onClick={() => choose(option.id)}
                      aria-label={`${option.name}, ${said.figure}${said.unit ? ` ${said.unit}` : ''}`}
                      className={cn('flex h-full w-full flex-col items-center gap-1.5 text-center',
                        phone ? 'px-2.5 pb-3.5 pt-3' : 'px-4 pb-4 pt-4')}
                    >
                      <span className="flex h-6 items-center">
                        {option.recommended && <ProposalTag kind="recommended" />}
                      </span>
                      <b className={cn('line-clamp-2 min-h-[2.5em] break-words leading-[1.25] text-text',
                        '[text-wrap:balance]', phone ? 'text-[15px]' : 'text-[17px]')}>
                        {option.name}
                      </b>
                      {/*
                        * The figure on a line of its own and never broken, the
                        * unit under it: "$102.86 per visit" in a 132px column
                        * put "visit" on a third line beside the price.
                        */}
                      <span className="flex flex-col items-center leading-[1.15]">
                        <b className={cn('tnum whitespace-nowrap tracking-[-0.02em] text-text',
                          phone ? 'text-[20px]' : 'text-[26px]')}>
                          {said.figure}
                        </b>
                        <span className="whitespace-nowrap text-[12.5px] font-semibold text-text-body">
                          {said.unit ? (phone ? shortUnit(said.unit) : said.unit) : '\u00a0'}
                        </span>
                      </span>
                      <span className="tnum text-[12px] text-text-muted">
                        {option.price.plan.unit && said.sub ? said.sub : countWords(option.visitIds.length, true)}
                      </span>
                      {option.tagline && !phone && (
                        <span className="line-clamp-2 text-[12.5px] leading-[1.4] text-text-muted">
                          {option.tagline}
                        </span>
                      )}
                    </button>
                  </div>
                )
              })}
            </div>

            {rows.map((row) => (
              <div key={row.key} role="row" className="contents">
                <div role="rowheader"
                  className={cn(first, 'flex flex-col justify-center gap-0.5 border-b border-line-soft py-3')}>
                  <span className={cn('font-semibold text-text', phone ? 'text-[14px]' : 'text-[14.5px]')}>
                    {row.name}
                  </span>
                  {row.about && (
                    <span className={cn('leading-[1.4] text-text-muted', phone ? 'text-[12px]' : 'text-[12.5px]')}>
                      {row.about}
                    </span>
                  )}
                </div>
                {group.options.map((option) => (
                  <div key={option.id} role="cell"
                    onClick={() => choose(option.id)}
                    className={cn(cell, 'flex cursor-pointer items-center justify-center border-b',
                      'border-line-soft', tone(option.id))}>
                    {row.in.has(option.id)
                      ? (
                        <span aria-label="included"
                          className={cn('flex h-6 w-6 items-center justify-center rounded-full',
                            picked === option.id ? 'bg-brand text-brand-on' : 'bg-brand-t2 text-brand-tx')}>
                          <GlyphCheck size={13} />
                        </span>
                        )
                      : <span aria-label="not included" className="text-[15px] text-text-faint">—</span>}
                  </div>
                ))}
              </div>
            ))}

            {/* The foot: one Choose per column, the column's width (step 21 item 4.6). */}
            <div role="row" className="contents">
              <div role="cell" className={cn(first, 'py-4')} />
              {group.options.map((option) => (
                <div key={option.id} role="cell"
                  className={cn(cell, tone(option.id), phone ? 'p-2.5' : 'px-4 py-4')}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={picked === option.id}
                    onClick={() => choose(option.id)}
                    className={cn('flex h-11 w-full items-center justify-center gap-1.5 rounded-[11px] px-2',
                      'whitespace-nowrap text-[14px] font-[650]',
                      picked === option.id
                        ? 'bg-brand text-brand-on'
                        : option.recommended
                          ? 'border-[1.5px] border-brand-ui bg-surface text-brand-tx'
                          : 'border-[1.5px] border-line-strong bg-surface text-text')}
                  >
                    {picked === option.id && <GlyphCheck size={15} />}
                    <span className="truncate">{picked === option.id ? 'Selected' : `Choose ${option.name}`}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/*
        * **None of these** is a plain row at the end, not a column: it is the
        * absence of a plan, and giving it the same furniture as the real ones
        * would read as one more thing on offer (board 4).
        */}
      {group.allowNone && (
        <button
          type="button"
          role="radio"
          aria-checked={picked === null}
          onClick={() => choose(null)}
          className={cn('flex min-h-[52px] items-center gap-3 rounded-[14px] px-4 text-left',
            picked === null
              ? 'border-2 border-brand-ui bg-brand-t1'
              : 'border border-line bg-surface')}
        >
          <Radio on={picked === null} />
          <span className="flex-1 text-[15px] font-semibold text-text">None of these</span>
          <span className="text-[14px] text-text-muted">Skip</span>
        </button>
      )}
    </div>
  )
}

/**
 * The rows: every visit some answer holds, in the page's order, each with
 * what it is under its name — or, where every answer is one visit, the
 * services across them, so Classic and Premium are compared by what is in
 * them rather than by which single trip each one is.
 */
function rowsOf(view: ProposalView, group: ProposalGroup): MatrixRow[] {
  const named = new Map(view.visits.map((visit) => [visit.id, visit]))
  const single = group.options.length > 0 && group.options.every((one) => one.visitIds.length === 1)

  if (single) {
    const order: string[] = []
    const holders = new Map<string, Set<string>>()
    for (const option of group.options) {
      const visit = named.get(option.visitIds[0]!)
      for (const service of visit?.services ?? []) {
        if (service.optional) continue
        if (!holders.has(service.name)) { holders.set(service.name, new Set()); order.push(service.name) }
        holders.get(service.name)!.add(option.id)
      }
    }
    if (order.length > 0) {
      return order.map((name) => ({ key: `s:${name}`, name, about: null, in: holders.get(name)! }))
    }
  }

  const inAny = new Set(group.options.flatMap((one) => one.visitIds))
  return view.visits
    .filter((visit) => inAny.has(visit.id))
    .map((visit) => ({
      key: visit.id,
      name: visit.name,
      about: aboutOf(visit),
      in: new Set(group.options.filter((one) => one.visitIds.includes(visit.id)).map((one) => one.id)),
    }))
}

/** The visit's own description, else what it is made of — never blank where there is something to say. */
function aboutOf(visit: ProposalVisit): string | null {
  if (visit.description.trim()) return visit.description.trim()
  const names = visit.services.filter((service) => !service.optional).map((service) => service.name)
  /* "Core treatment" under "Core treatment" says nothing twice. */
  if (names.length === 0 || (names.length === 1 && names[0] === visit.name)) return null
  return names.join(' · ')
}

function Radio({ on }: { on: boolean }) {
  return on
    ? (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2
                       border-brand-ui bg-surface">
        <i className="block h-3 w-3 rounded-full bg-brand-ui" />
      </span>
      )
    : <span className="block h-6 w-6 shrink-0 rounded-full border-[1.6px] border-text-faint bg-surface" />
}

/** "one visit", "5 visits" — lower case under a price, title case in a chip. */
function countWords(n: number, lower = false): string {
  if (n === 1) return lower ? 'one visit' : '1 visit'
  return `${n} visits`
}

/** "/ month" → "/ mo" where a phone row has room for one short word. */
const shortUnit = (unit: string): string =>
  unit.replace('/ month', '/ mo').replace('/ week', '/ wk')

/**
 * An option is not a visit, but the way its money is *said* is exactly a
 * visit's — plan, per visit, or both — so it borrows the one wording rather
 * than growing a second that could word the same figure differently.
 */
function standIn(view: ProposalView, option: ProposalOption): ProposalVisit {
  return {
    id: option.id, name: option.name, description: '', kind: 'choice',
    groupId: null, optionIds: [option.id], recommended: option.recommended,
    count: { n: option.price.occurrences, unit: 'visits', openEnded: false },
    timing: {
      mode: 'count', phrase: '', facts: '', frequency: null,
      season: null, dates: [], project: null, rule: null,
    },
    price: option.price,
    detail: view.settings.detail, kinds: [], showHours: false,
    recurring: null, oneTime: null, services: [],
  }
}
